import VerticalSpacer from 'components/Common/Spacing/VerticalSpacer'
import { LOCATION_SEARCH_ERROR_MESSAGE, LOCATION_SEARCH_INPUT_PLACEHOLDER_LABEL } from 'constants/search'
import { EmptyArray } from 'lib/array/arrayUtils'
import React, { useCallback, useImperativeHandle, useRef, useState } from 'react'
import SearchPlaceSelectList from './SearchPlaceSelectList'
import IconButton from 'components/Luxkit/Button/IconButton'
import LineTimesIcon from 'components/Luxkit/Icons/line/LineTimesIcon'
import noop from 'lib/noop'
import TextInputOnly from 'components/Common/Form/Input/TextInputOnly'
import LineSearchIcon from 'components/Luxkit/Icons/line/LineSearchIcon'
import Heading from 'components/Luxkit/Typography/Heading'
import SearchMenuListItem from '../SearchForm/SearchMenu/SearchMenuListItem'
import HiddenInput from 'components/Common/Form/Input/HiddenInput'
import styled from 'styled-components'

const PlaceControl = styled(VerticalSpacer)`
  position: relative;
  width: 100%;
`

interface Props {
  className?: string;
  label?: string;
  value?: App.SearchItem;
  placeholder?: string;
  searchTypes?: Array<App.SearchType>;
  placeTypes?: Array<App.SearchPlaceType>;
  /* Items to show when a user hasn't entered any search */
  placeholderItems?: Array<App.SearchItem>;
  placeholderTitle?: string;
  inputRef?: React.Ref<HTMLInputElement | null>;
  /* When the selected item changes */
  onChange?: (item?: App.SearchItem) => void;
  onResultsChange?: (results: Array<App.SearchItem>, searchPhrase: string) => void;
  required?: boolean;
  searchVertical?: App.SearchPlaceVertical;
  recentSearchItems?: Array<App.FullRecentSearch>;
  /** Use this to supply your own search results list the input will use */
  overrideSearchItems?: Array<App.SearchItem>;
}

function SearchPlaceSelectInline(props: Props) {
  const {
    searchTypes,
    placeholder = LOCATION_SEARCH_INPUT_PLACEHOLDER_LABEL,
    className,
    placeTypes,
    value,
    placeholderItems = EmptyArray,
    onChange = noop,
    onResultsChange = noop,
    placeholderTitle,
    inputRef,
    searchVertical,
    required,
    overrideSearchItems,
  } = props

  const [searchPhrase, setSearchPhrase] = useState<string>(value?.format.mainText ?? '')
  const localInputRef = useRef<HTMLInputElement>(null)

  useImperativeHandle(inputRef, () => localInputRef.current)
  const onClear = useCallback<React.MouseEventHandler<HTMLButtonElement>>(() => {
    if (localInputRef.current) {
      localInputRef.current.value = ''
    }
    setSearchPhrase('')
    onChange(undefined)
  }, [onChange])

  const onLocationSelect = useCallback((searchItem?: App.SearchItem) => {
    if (localInputRef.current) {
      localInputRef.current.value = ''
    }
    setSearchPhrase('')
    onChange(searchItem)
  }, [onChange])

  const showPlaceholder = searchPhrase.length === 0 || (!!value && value.format.mainText === searchPhrase)

  return <PlaceControl gap={12} className={className}>
    {!!required && <HiddenInput
      value={value ? 'filled' : undefined}
      required
      invalidMessage={LOCATION_SEARCH_ERROR_MESSAGE}
    />}
    <TextInputOnly
      noValidationSpacing
      placeholder={placeholder}
      inputRef={localInputRef}
      defaultValue={value?.format.mainText}
      onChange={e => setSearchPhrase(e.currentTarget.value)}
      endIcon={searchPhrase.length > 0 ?
        <IconButton kind="tertiary" onClick={onClear}><LineTimesIcon /></IconButton> :
        <LineSearchIcon />
      }
    />
    {showPlaceholder && <VerticalSpacer gap={12}>
      {placeholderTitle && <Heading variant="heading6">{placeholderTitle}</Heading>}
      <div>
        {placeholderItems.map(item => <SearchMenuListItem
          key={item.value}
          searchItem={item}
          onClick={onChange}
        />)}
      </div>
    </VerticalSpacer>}
    {!showPlaceholder && <SearchPlaceSelectList
      searchPhrase={searchPhrase}
      onResultsChange={onResultsChange}
      onChange={onLocationSelect}
      placeTypes={placeTypes}
      searchTypes={searchTypes}
      vertical={searchVertical}
      overrideSearchItems={overrideSearchItems}
    />}
  </PlaceControl>
}

export default SearchPlaceSelectInline
